// src/components/InitSuperAdminForm.js
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  Button,
  Container,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import axiosInstance from "../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { logger } from "../utils/logger";

const InitSuperAdminForm = ({ onSuperadminCreated }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
  });
  const [feedback, setFeedback] = useState({ message: "", severity: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    logger.info("Init Superadmin form opened");
    return () => {
      logger.info("Init Superadmin form closed");
    };
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Validate form fields
  const formValidation = () => {
    if (!formData.username || !formData.email || !formData.password) {
      setFeedback({ message: "All fields are required.", severity: "error" });
      logger.warn("Superadmin creation validation error", { formData });
      return false;
    }
    if (formData.password.length < 6) {
      setFeedback({
        message: "Password must be at least 6 characters long.",
        severity: "error",
      });
      logger.warn("Superadmin creation validation error", {
        username: formData.username,
        issue: "Password too short",
      });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      setFeedback({ message: "Email is not valid.", severity: "error" });
      logger.warn("Superadmin creation validation error", {
        username: formData.username,
        email: formData.email,
        issue: "Invalid email",
      });
      return false;
    }
    return true;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValidation()) return;
    setLoading(true);
    try {
      const response = await axiosInstance.post(
        "api/auth/init-superadmin",
        formData
      );
      setFeedback({ message: response.data.message, severity: "success" });

      logger.info("Superadmin created successfully", {
        username: formData.username,
        email: formData.email,
      });
      // Redirect to login page after success (with a small delay if needed)
      if (onSuperadminCreated) {
        setTimeout(() => {
          onSuperadminCreated();
        }, 1000);
      }
    } catch (err) {
      const errMsg =
        err.response?.data?.message || "Error initializing Superadmin";
      setFeedback({ message: errMsg, severity: "error" });

      logger.error("Superadmin creation failed", {
        username: formData.username,
        email: formData.email,
        error: errMsg,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setFeedback({ message: "", severity: "" });

  return (
    <Container maxWidth="xs">
      {/* Create Superadmin Form */}
      <form onSubmit={handleSubmit}>
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="username"
          label="Username"
          value={formData.username}
          onChange={handleChange}
          autoFocus
          autoComplete="username"
        />
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleChange}
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  data-log="Show/Hide Password Icon Button"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="email"
          label="Email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          autoComplete="email"
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 2 }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Create Superadmin"}
        </Button>
      </form>
      {/* Feedback Snackbar */}
      <Snackbar
        open={!!feedback.message}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={feedback.severity || "info"}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

InitSuperAdminForm.propTypes = {
  onSuperadminCreated: PropTypes.func,
};

export default InitSuperAdminForm;
